import React, { Suspense, useRef, useState, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Environment, Preload, Float, Stars } from '@react-three/drei'
import * as THREE from 'three'
import { EffectComposer, Bloom, Vignette, Noise } from '@react-three/postprocessing'
import { useAuth } from '../contexts/SupabaseAuthContext'
import { useNavigate } from 'react-router-dom'
import { HeroSectionHTML } from '../components/home/HeroSectionHTML'
import TokenVendingMachine from '../components/home/TokenVendingMachine'
import TokenExplanation from '../components/home/TokenExplanation'
import HeroCarousel2D from '../components/home/HeroCarousel2D'
import { TechTunnel } from '../components/canvas/TechTunnel'
import { CinematicBackground } from '../components/canvas/CinematicBackground'
import { AnimatePresence, motion } from 'framer-motion'
import FloatingSidebar from '../components/FloatingSidebar'
import TestimonialCarousel from '../components/home/TestimonialCarousel'
import { ShieldCheck, Users, Zap, ArrowRight } from 'lucide-react'

// --- CONTROLE DE CÂMERA SINCRONIZADO COM SCROLL ---
function CameraRig() {
  const { camera } = useThree()
  const vec = new THREE.Vector3()
  
  useFrame((state) => {
    // Ler scroll diretamente do window para performance máxima e simplicidade
    const scrollY = window.scrollY
    const vh = window.innerHeight
    
    // Definição de "Cenas" baseada na posição do scroll (em pixels)
    // Scene 0: Hero (0 - 1vh) -> Camera neutra
    // Scene 1: Tower (1vh - 2.5vh) -> Camera foca na torre
    // Scene 2: Tokens (2.5vh - 3.5vh) -> Camera se afasta/fundo
    
    // Alvos de posição da câmera para cada cena
    let targetPos = new THREE.Vector3(0, 0, 20) // Posição Inicial (Hero)
    let targetLookAt = new THREE.Vector3(0, 0, 0)
    
    if (scrollY < vh * 0.5) {
      // HERO SECTION
      // Camera flutua levemente
      targetPos.set(0, 0, 18 + Math.sin(state.clock.elapsedTime * 0.2) * 2)
      targetLookAt.set(0, 0, 0)
    } else if (scrollY >= vh * 0.5 && scrollY < vh * 2.5) {
      // TOWER SECTION (Scroll entre 0.5 e 2.5 telas)
      // A torre está em Y = -30 (definido no HeroTower wrapper abaixo)
      // Queremos chegar perto dela
      targetPos.set(0, -30, 22) // Zoom na torre
      targetLookAt.set(0, -30, 0)
    } else {
      // OUTRAS SEÇÕES (Tokens, Launcher)
      // Camera desce mais ou vai para um ponto neutro
      targetPos.set(0, -60, 40)
      targetLookAt.set(0, -60, 0)
    }

    // Interpolação suave (Lerp)
    camera.position.lerp(targetPos, 0.05)
    
    // Smooth LookAt
    // Criamos um vetor temporário para onde a câmera está olhando atualmente
    // e interpolamos para o novo lookAt
    // Simplificação: apenas lerp na posição e lookAt fixo por frame funciona bem se suave
    // Mas para lookAt suave manual:
    const currentLookAt = new THREE.Vector3(0, 0, -10).applyQuaternion(camera.quaternion).add(camera.position)
    currentLookAt.lerp(targetLookAt, 0.05)
    camera.lookAt(currentLookAt)
  })

  return null
}

// --- PÁGINA PRINCIPAL ---
export default function HomeIntroPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  // Função para scroll suave até uma seção
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="relative w-full bg-black min-h-screen">
      <FloatingSidebar />
      
      {/* 1. CAMADA 3D (FIXA NO FUNDO) */}
      <div className="fixed inset-0 z-0 pointer-events-auto">
        <Canvas
          gl={{ antialias: false, powerPreference: "high-performance", alpha: false }}
          dpr={[1, 1.5]} // Otimização de pixel ratio
          camera={{ position: [0, 0, 20], fov: 35, near: 0.1, far: 1000 }}
        >
          <color attach="background" args={['#050505']} />
          
          {/* Controle da Câmera */}
          <CameraRig />
          
          {/* Iluminação Global */}
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={1} />
          
          {/* Elementos da Cena */}
          <Suspense fallback={null}>
            {/* Fundo Galáctico/Cinemático */}
            <CinematicBackground />
            <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />

            {/* Elementos Decorativos Extras (Tech Tunnel lá no fundo/baixo) */}
            <group position={[0, -80, 0]}>
               <TechTunnel />
            </group>

          </Suspense>

          {/* Pós-processamento - TEMPORARILY DISABLED FOR DEBUGGING
          <EffectComposer disableNormalPass>
            <Bloom luminanceThreshold={0.5} mipmapBlur intensity={1.5} radius={0.6} />
            <Noise opacity={0.05} />
            <Vignette eskil={false} offset={0.1} darkness={1.1} />
          </EffectComposer>
          */}
          
          <Preload all />
        </Canvas>
      </div>

      {/* 2. CAMADA DE CONTEÚDO HTML (SCROLLÁVEL) */}
      {/* Z-Index maior que o Canvas para ficar por cima. Pointer-events-none nos containers vazios para deixar clicar no 3D */}
      <div className="relative z-10 flex flex-col w-full">

        {/* SECTION 1: HERO (HTML Puro) */}
        <section id="hero" className="h-screen w-full flex items-center justify-center p-6 relative pointer-events-none">
          {/* Conteúdo interativo deve ter pointer-events-auto */}
          <div className="pointer-events-auto w-full">
            <HeroSectionHTML onExploreClick={() => scrollToSection('tower-view')} />
          </div>
        </section>

        {/* SECTION 2: CAROUSEL 2D */}
        <section id="tower-view" className="min-h-screen w-full relative z-20 pointer-events-auto bg-black/50 backdrop-blur-sm">
           <HeroCarousel2D />
        </section>

        {/* SECTION 3: TOKENS (Vending Machine) */}
        {/* Fundo semi-transparente para garantir legibilidade sobre o 3D */}
        <section id="tokens" className="min-h-screen w-full flex items-center justify-center p-4 md:p-20 bg-black/60 backdrop-blur-sm border-t border-white/5 relative z-20">
          <div className="w-full max-w-7xl pointer-events-auto">
            <TokenExplanation />
            
            <div className="mb-12 text-center">
              <h2 className="text-4xl md:text-6xl font-black text-white mb-4">TOKENS & CRÉDITOS</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Adquira tokens para usar nos nossos serviços de IA. Instantâneo via Pix.
              </p>
            </div>
            <TokenVendingMachine 
              navigate={navigate} 
              onOpenLogin={() => navigate('/auth/login')}
            />

            {/* SOCIAL PROOF SECTION */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
            >
              <div className="flex flex-col items-center text-center p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <ShieldCheck className="w-10 h-10 text-cyan-400 mb-4" />
                <h3 className="text-white font-bold mb-2">Compra Segura</h3>
                <p className="text-gray-400 text-sm">Pagamentos processados via Pix com liberação imediata dos tokens.</p>
              </div>
              <div className="flex flex-col items-center text-center p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <Users className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-white font-bold mb-2">Comunidade Ativa</h3>
                <p className="text-gray-400 text-sm">Centenas de designers e estúdios já utilizam nossa tecnologia.</p>
              </div>
              <div className="flex flex-col items-center text-center p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <Zap className="w-10 h-10 text-yellow-400 mb-4" />
                <h3 className="text-white font-bold mb-2">Alta Performance</h3>
                <p className="text-gray-400 text-sm">Servidores otimizados para gerar resultados em segundos.</p>
              </div>
            </motion.div>

            {/* TESTIMONIALS CAROUSEL */}
            <TestimonialCarousel />

            {/* STORE CTA BUTTON */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mt-16 text-center"
            >
              <button
                onClick={() => navigate('/softwares')}
                className="group relative px-10 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full text-white font-bold text-xl overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(6,182,212,0.5)]"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <span className="relative flex items-center gap-3">
                  Explorar Loja Completa
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <p className="mt-4 text-gray-500 text-sm">Descubra todas as nossas ferramentas de IA</p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: LAUNCHER REMOVED */}

        {/* FOOTER SIMPLES */}
        <footer className="w-full py-12 bg-black text-center text-gray-600 border-t border-white/10 relative z-20">
          <p>© 2025 Overpixel. Todos os direitos reservados.</p>
        </footer>

      </div>

    </div>
  )
}
